/**
 * Le chat, côté client.
 *
 * On ne garde en mémoire que ce qui est affiché, et on ne demande au serveur
 * que ce qui est arrivé **depuis le dernier message connu** : redemander tout
 * le salon toutes les cinq secondes serait absurde pour trois lignes nouvelles.
 */
export interface Message {
  id: number
  pseudo: string
  texte: string
  le: number
}

const LIMITE = 200
/**
 * Jusqu'où on avait lu.
 *
 * La liste des messages ne vit qu'en mémoire : après un rechargement, le salon
 * entier revenait comme « nouveau » et le compteur de non-lus affichait cinq
 * messages qu'on avait déjà vus. On retient donc l'identifiant du dernier lu,
 * là où il survit à la page.
 */
const CLE_LU = "dofusidle.chatLu"

const dernierLu = (): number => Number(localStorage.getItem(CLE_LU) ?? 0) || 0

/** Marque tout ce qui est chargé comme lu. Appelé dès que le chat est visible. */
export function marquerLu(): void {
  const dernier = messages.length > 0 ? messages[messages.length - 1]!.id : 0
  if (dernier > dernierLu()) localStorage.setItem(CLE_LU, String(dernier))
}

/** Combien de messages chargés n'ont pas encore été vus. */
export const nonLus = (): number => {
  const lu = dernierLu()
  return messages.filter((m) => m.id > lu).length
}

let messages: Message[] = []

export const lus = (): Message[] => messages

/** Récupère ce qui est arrivé depuis le dernier message connu. */
export async function rafraichir(): Promise<{ nouveaux: number; erreur?: string }> {
  const dernier = messages.length > 0 ? messages[messages.length - 1]!.id : 0
  try {
    const r = await fetch(`/api/chat?depuis=${dernier}`)
    const d = (await r.json()) as { messages?: Message[]; erreur?: string }
    if (!r.ok) return { nouveaux: 0, erreur: d.erreur ?? "Le chat n'a pas répondu." }
    const arrives = d.messages ?? []
    if (arrives.length > 0) {
      // Le salon ne garde qu'une fenêtre : sans quoi une longue session finirait
      // par tenir des milliers de lignes en mémoire, et autant dans le DOM.
      messages = [...messages, ...arrives].slice(-LIMITE)
      // Première visite : rien n'a jamais été lu, mais rien n'a été manqué non
      // plus. Sans cela, un nouveau venu voyait soixante messages « non lus ».
      if (!localStorage.getItem(CLE_LU)) marquerLu()
    }
    return { nouveaux: arrives.length }
  } catch {
    return { nouveaux: 0, erreur: "Le chat n'a pas répondu. Vérifie ta connexion." }
  }
}

export async function envoyer(id: string, texte: string): Promise<{ erreur?: string }> {
  try {
    const r = await fetch("/api/chat", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ id, texte }),
    })
    const d = (await r.json()) as Message & { erreur?: string }
    if (!r.ok) return { erreur: d.erreur ?? "Message refusé." }
    // On l'ajoute tout de suite : attendre le prochain rafraîchissement
    // donnerait l'impression que le message s'est perdu.
    if (!messages.some((m) => m.id === d.id)) messages = [...messages, d].slice(-LIMITE)
    // Son propre message est lu par définition.
    marquerLu()
    return {}
  } catch {
    return { erreur: "Message non parti. Vérifie ta connexion." }
  }
}

export async function signaler(id: string, message: number): Promise<void> {
  try {
    await fetch("/api/signaler", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ id, message }),
    })
  } catch { /* un signalement perdu n'est pas grave */ }
}
